import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "ach",
    "achButton",
    "achContainer",
    "card",
    "cardButton",
    "cardContainer",
    "cardError",
    "name",
    "select",
    "token",
  ]
  static values = {
    publicKey: String,
    src: String,
    url: String,
  }

  computeStyles = () => {
    const fieldGap = 1.5
    const style = getComputedStyle(this.nameTarget)
    const inputStyles = {
      "background-color": style.getPropertyValue("background-color"),
      "border-radius": style.getPropertyValue("border-radius"),
      "font-family": style.getPropertyValue("font-family"),
      "font-size": style.getPropertyValue("font-size"),
      "font-weight": style.getPropertyValue("font-weight"),
      "line-height": style.getPropertyValue("line-height"),
      border: style.getPropertyValue("border"),
      color: style.getPropertyValue("color"),
      height: "auto",
      margin: style.getPropertyValue("margin"),
      padding: style.getPropertyValue("padding"),
      transition: "border-color 0.3s ease, box-shadow 0.3s ease",
    }

    const selectStyle = getComputedStyle(this.selectTarget)
    const selectStyles = {
      ...inputStyles,
      "-moz-appearance": "none",
      "-webkit-appearance": "none",
      appearance: "none",
      "background-image": selectStyle.getPropertyValue("background-image"),
      "background-position-x": "calc(100% - 8px)",
      "background-position-y": "50%",
      "background-repeat": selectStyle.getPropertyValue("background-repeat"),
      "background-size": selectStyle.getPropertyValue("background-size"),
      padding: "8px",
      "padding-right": "16px",
    }
    const styles = {
      ".card .cc input, input": inputStyles,
      ".ach .fieldsetrow, .card": {
        gap: `${fieldGap}rem`,
      },
      ".card > div:not(:last-child):not(.break)": {
        padding: "0",
      },
      ".ach .fieldsetrow .break, .break": {
        display: "none",
      },
      ".fieldset, .fieldsetrow": {
        padding: "0",
      },
      ".first_name label, .last_name label": {
        display: "none",
      },
      ".fieldset.first_name": {
        "padding-top": "8px !important",
      },
      "input:focus,select:focus": {
        "box-shadow": "0 0 0 0.175rem black inset",
        outline: "none",
      },
      ".billing label": {
        display: "none",
      },
      ".billing > div:last-child": {
        display: "none",
      },
      ".card .cc input": {
        "padding-left": "2.75rem",
      },
      ".card .cc .cc-icon": {
        left: "0.8rem",
        padding: "0",
        top: "0",
      },
      ".card .cvv": {
        "flex-basis": `calc(33.333% - ${fieldGap * 0.5}rem)`,
      },
      ".card .exp": {
        "flex-basis": `calc(66.667% - ${fieldGap * 0.5}rem)`,
      },
      select: selectStyles,
    }

    return styles
  }

  connect() {
    this.init().then(this.loadFrame)
    this.selectedType = "card"
  }

  ensureToken = (e) => {
    if (this.tokenTarget.value.length < 5) {
      e.preventDefault()
      window.confirmMethod(
        "Account invalid",
        e.currentTarget,
        `Please enter your ${this.selectedType === "ach" ? "bank account" : "card"} information`
      )
    }
  }

  init() {
    return new Promise((resolve, reject) => {
      if (window.Tokenizer) {
        return resolve()
      }

      const script = document.createElement("script")
      script.src = this.srcValue
      script.onerror = reject
      script.onload = () => {
        resolve()
      }
      document.head.appendChild(script)
    })
  }

  loadFrame = () => {
    this.styles = this.computeStyles()
    this.ach = this.newToken("ach", this.achTarget)
    this.card = this.newToken("card", this.cardTarget)
  }

  newToken = (type, container) => {
    return new Tokenizer({
      apikey: this.publicKeyValue,
      container,
      settings: {
        payment: {
          ach: {
            secCodeList: ["ppd"],
            sec_code: "ppd",
            showSecCode: false,
          },
          card: { requireCVV: true },
          types: [type],
        },
        styles: this.styles,
      },
      submission: this.tokenSubmission,
      url: this.urlValue,
    })
  }

  sanitizeName = (e) => {
    e.currentTarget.value = e.currentTarget.value.replace(/[^a-z -]/gi, "")
  }

  showAch = (e) => {
    this.selectedType = "ach"
    this.achContainerTarget.classList.remove("hidden")
    this.achButtonTarget.classList.remove("bg-white", "cursor-pointer", "hover:bg-gray-100")
    this.achButtonTarget.classList.add("bg-black", "text-white", "cursor-default")

    this.cardContainerTarget.classList.add("hidden")
    this.cardButtonTarget.classList.add("bg-white", "cursor-pointer", "hover:bg-gray-100")
    this.cardButtonTarget.classList.remove("text-white", "cursor-default")

    this.element.querySelectorAll("[data-ach-required]").forEach((container) => {
      container.querySelectorAll("input, select").forEach((input) => {
        input.required = true
      })
      container.classList.remove("hidden")
    })
  }

  showCard = (e) => {
    this.selectedType = "card"
    this.cardContainerTarget.classList.remove("hidden")
    this.cardButtonTarget.classList.remove("bg-white", "cursor-pointer", "hover:bg-gray-100")
    this.cardButtonTarget.classList.add("bg-black", "text-white", "cursor-default")

    this.achContainerTarget.classList.add("hidden")
    this.achButtonTarget.classList.add("bg-white", "cursor-pointer", "hover:bg-gray-100")
    this.achButtonTarget.classList.remove("text-white", "cursor-default")

    this.element.querySelectorAll("[data-ach-required]").forEach((container) => {
      container.querySelectorAll("input, select").forEach((input) => {
        input.required = false
      })
      container.classList.add("hidden")
    })
  }

  submit = (e) => {
    // NOTE: removed since it seems like this was causing issues with form resubmission
    // after a validation error. Leaving it commented out for now.
    // if (this.tokenTarget.value.length > 5) {
    //   this.element.closest("form").requestSubmit()
    //   return
    // }

    e.currentTarget.disabled = true

    if (this.selectedType === "ach") {
      this.ach.submit()
    }
    if (this.selectedType === "card") {
      this.card.submit()
    }
  }

  submitAch = (e) => {
    this.submit(e)
  }
  submitCard = (e) => {
    this.submit(e)
  }

  tokenSubmission = (resp) => {
    setTimeout(() => {
      this.element.querySelectorAll("button").forEach((b) => (b.disabled = false))
    }, 4000)

    switch (resp.status) {
      case "success":
        this.tokenTarget.value = resp.token
        this.cardTarget.innertText = "Card added"
        this.achTarget.innertText = "Bank account added"

        if (this.element.dataset.selfSignUp == "true") {
          this.dispatch("success", { prefix: "tokenized-status" })
        }

        if (this.element.dataset.submitOnToken == "true") {
          this.tokenTarget.closest("form").requestSubmit()
        }

        break
      case "error":
        console.log("Card Error", { resp })
        break
      case "validation":
        if (resp.invalid.includes("cc")) {
          this.cardErrorTarget.classList.remove("hidden")
        } else {
          this.cardErrorTarget.classList.add("hidden")
        }
        break
      default:
        console.log("Error", { resp })
    }
  }
}
